import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Center, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { IconLock, IconRotate } from '@tabler/icons-react';
import { GOOGLE_MAPS_API_KEY } from '@mdev/common';
import { randomStreetView } from './services/randomStreetView';

type GameStatus = 'active' | 'finished';
type LatLng = { lat: number; lng: number };

const ZERO: LatLng = { lat: 0, lng: 0 };
const MAPS_LIBRARIES: ('geometry' | 'places' | 'drawing' | 'visualization')[] = ['geometry'];

const haversineMiles = (a: LatLng, b: LatLng): number => {
  const R = 6378.137;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  const km = R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return km * 0.621371192;
};

const calculateScore = (miles: number): number => {
  const mapSize = 935;
  return Math.round(5000 * Math.E ** ((-1 * miles) / mapSize));
};

const formatDistance = (miles: number): string => {
  if (miles < 1) return Math.round((miles * 5280) / 3) + ' yards';
  if (miles < 25) return miles.toFixed(1) + ' miles';
  return miles.toFixed(0) + ' miles';
};

export function GeoguesserPage() {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: MAPS_LIBRARIES,
  });

  const [actualCoords, setActualCoords] = useState<LatLng | null>(null);
  const [guessCoords, setGuessCoords] = useState<LatLng | null>(null);
  const [status, setStatus] = useState<GameStatus>('active');
  const [distance, setDistance] = useState<number | null>(null);
  const [score, setScore] = useState<number | null>(null);
  const [resetKey, setResetKey] = useState(0);

  const panoRef = useRef<HTMLDivElement | null>(null);
  const panoInstanceRef = useRef<google.maps.StreetViewPanorama | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  // Initialize the random-SV finder once Maps is loaded.
  useEffect(() => {
    if (!isLoaded) return;
    randomStreetView.init(window.google.maps);
    randomStreetView.setParameters({});
  }, [isLoaded]);

  // Fetch a random location whenever the round resets.
  useEffect(() => {
    if (!isLoaded) return;
    let cancelled = false;
    setActualCoords(null);
    randomStreetView
      .getRandomLocation()
      .then((coords) => {
        if (!cancelled && coords) setActualCoords(coords);
      })
      .catch((err) => console.error(err));
    return () => {
      cancelled = true;
    };
  }, [isLoaded, resetKey]);

  // Spawn the panorama imperatively once we have an actual location.
  useEffect(() => {
    if (!isLoaded || !actualCoords || !panoRef.current) return;
    if (!panoInstanceRef.current) {
      panoInstanceRef.current = new window.google.maps.StreetViewPanorama(panoRef.current, {
        position: actualCoords,
        addressControl: false,
        showRoadLabels: false,
        fullscreenControl: false,
        zoomControl: true,
      });
    } else {
      panoInstanceRef.current.setPosition(actualCoords);
    }
  }, [isLoaded, actualCoords]);

  // Fit map bounds when the round ends so both pins are visible.
  useEffect(() => {
    if (status !== 'finished' || !mapRef.current || !actualCoords || !guessCoords) return;
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(actualCoords);
    bounds.extend(guessCoords);
    mapRef.current.fitBounds(bounds, 60);
  }, [status, actualCoords, guessCoords]);

  const handleMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (status !== 'active' || !event.latLng || !actualCoords) return;
      const guess: LatLng = { lat: event.latLng.lat(), lng: event.latLng.lng() };
      setGuessCoords(guess);
      const miles = haversineMiles(actualCoords, guess);
      setDistance(miles);
      setScore(calculateScore(miles));
    },
    [status, actualCoords],
  );

  const handleConfirm = () => setStatus('finished');

  const handlePlayAgain = () => {
    setStatus('active');
    setActualCoords(null);
    setGuessCoords(null);
    setDistance(null);
    setScore(null);
    setResetKey((k) => k + 1);
  };

  if (!isLoaded) {
    return (
      <Center mih="80vh">
        <Loader />
      </Center>
    );
  }

  return (
    <Stack gap={0} h="calc(100vh - 88px)" style={{ overflow: 'hidden' }}>
      <Box
        ref={panoRef}
        style={{
          flex: '3 1 0',
          minHeight: 0,
          width: '100%',
          background: '#000',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        {!actualCoords && (
          <Center h="100%">
            <Stack align="center" gap="xs">
              <Loader />
              <Text c="dimmed" size="sm">
                Finding a random street view location…
              </Text>
            </Stack>
          </Center>
        )}
      </Box>

      <Box style={{ flex: '2 1 0', minHeight: 0, position: 'relative', marginTop: 8 }}>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          zoom={2}
          center={ZERO}
          options={{
            fullscreenControl: false,
            mapTypeControl: false,
            streetViewControl: false,
            disableDefaultUI: false,
          }}
          onLoad={(map) => {
            mapRef.current = map;
          }}
          onClick={handleMapClick}
        >
          {guessCoords && (
            <Marker
              position={guessCoords}
              label={{ text: 'Guess', color: '#fff', fontSize: '12px', fontWeight: 'bold' }}
            />
          )}
          {status === 'finished' && actualCoords && (
            <Marker
              position={actualCoords}
              label={{ text: 'Actual', color: '#fff', fontSize: '12px', fontWeight: 'bold' }}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                fillColor: '#339af0',
                fillOpacity: 1,
                strokeColor: '#fff',
                strokeWeight: 2,
                scale: 10,
              }}
            />
          )}
        </GoogleMap>

        {status === 'finished' && distance !== null && score !== null && (
          <Paper
            shadow="md"
            p="sm"
            style={{
              position: 'absolute',
              top: 8,
              left: 8,
              background: 'rgba(0,0,0,0.78)',
              color: '#fff',
              borderRadius: 8,
              minWidth: 180,
              zIndex: 5,
            }}
          >
            <Title order={5} c="white" mb={4}>
              Distance: {formatDistance(distance)}
            </Title>
            <Text c="white" fw={600}>
              Score: {score}
            </Text>
          </Paper>
        )}

        <Box style={{ position: 'absolute', bottom: 8, left: 8, zIndex: 5 }}>
          {status === 'active' ? (
            <Button
              color="red"
              leftSection={<IconLock size={16} />}
              disabled={!guessCoords}
              onClick={handleConfirm}
            >
              Confirm Guess
            </Button>
          ) : (
            <Button
              color="blue"
              leftSection={<IconRotate size={16} />}
              onClick={handlePlayAgain}
            >
              Play Again
            </Button>
          )}
        </Box>
      </Box>
    </Stack>
  );
}
